import { useRef } from 'react'
import type { ReplyTone } from '@/lib/types'
import { cn } from '@/lib/utils'

/** The 5 AI-suggestion registers, ordered from « familier » to « formel ». */
export const TONES: ReplyTone[] = [
  'tu_buddy',
  'tu_cordial',
  'vous_friendly',
  'vous_pro',
  'vous_formal',
]

/** Elegant discrete slider for the AI suggestion register. The 5 tones
 *  form a single axis from « familier » (left) to « formel » (right);
 *  the user drags the thumb (or uses arrow keys / clicks the track) and a
 *  card above shows the selected register's label + explanation. */
export function ToneSlider({
  value,
  disabled,
  onChange,
  t,
  compact = false,
}: {
  value: ReplyTone
  disabled: boolean
  onChange: (v: ReplyTone) => void
  t: (key: string) => string
  /** Compact mode hides the big description card (used in tight spaces
   *  like the messagerie composer) — the label is shown elsewhere. */
  compact?: boolean
}) {
  const n = TONES.length
  const idx = Math.max(0, TONES.indexOf(value))
  const pct = (idx / (n - 1)) * 100
  const trackRef = useRef<HTMLDivElement>(null)

  function setFromClientX(clientX: number) {
    const el = trackRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width))
    const next = Math.round(ratio * (n - 1))
    if (next !== idx) onChange(TONES[next])
  }

  return (
    <div className={cn(disabled && 'pointer-events-none opacity-50')}>
      {/* Selected register : label + explanation */}
      {!compact && (
        <div className="mb-3 rounded-xl border border-[var(--color-accent)] bg-[var(--color-accent-bg)] px-3 py-2.5">
          <div className="text-[12.5px] font-semibold text-[var(--color-text)]">
            {t(`projectSettings.tone.${value}`)}
          </div>
          <div className="mt-0.5 text-[11px] text-[var(--color-text-dim)]">
            {t(`projectSettings.tone.${value}Hint`)}
          </div>
        </div>
      )}

      {/* Slider track */}
      <div
        ref={trackRef}
        role="slider"
        aria-valuemin={0}
        aria-valuemax={n - 1}
        aria-valuenow={idx}
        aria-valuetext={t(`projectSettings.tone.${value}`)}
        aria-label={t('projectSettings.toneTitle')}
        tabIndex={disabled ? -1 : 0}
        onPointerDown={(e) => {
          if (disabled) return
          e.currentTarget.setPointerCapture(e.pointerId)
          setFromClientX(e.clientX)
        }}
        onPointerMove={(e) => {
          if (disabled || e.buttons === 0) return
          setFromClientX(e.clientX)
        }}
        onKeyDown={(e) => {
          if (disabled) return
          if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
            e.preventDefault()
            if (idx > 0) onChange(TONES[idx - 1])
          } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
            e.preventDefault()
            if (idx < n - 1) onChange(TONES[idx + 1])
          }
        }}
        className={cn(
          'relative mx-1 h-6 touch-none select-none rounded-lg outline-none',
          disabled ? 'cursor-not-allowed' : 'cursor-pointer',
          'focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]/40',
        )}
      >
        {/* Base + filled track */}
        <div className="absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-[var(--color-border)]" />
        <div
          className="absolute left-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-[var(--color-accent)]"
          style={{ width: `${pct}%` }}
        />
        {/* Step ticks */}
        {TONES.map((_, i) => {
          const p = (i / (n - 1)) * 100
          return (
            <span
              key={i}
              className={cn(
                'absolute top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full',
                i <= idx
                  ? 'bg-[var(--color-accent)]'
                  : 'bg-[var(--color-surface)] ring-1 ring-[var(--color-border)]',
              )}
              style={{ left: `${p}%` }}
            />
          )
        })}
        {/* Thumb */}
        <span
          className="absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[var(--color-accent)] bg-[var(--color-surface)] shadow-sm transition-[left] duration-100"
          style={{ left: `${pct}%` }}
        />
      </div>

      {/* Scale endpoints */}
      <div className="mt-2 flex justify-between px-1 text-[10.5px] font-medium uppercase tracking-wide text-[var(--color-text-dim)]">
        <span>{t('projectSettings.tone.scaleLeft')}</span>
        <span>{t('projectSettings.tone.scaleRight')}</span>
      </div>
    </div>
  )
}
